import { m } from 'framer-motion';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';

// components
import { MotionViewport, varFade } from 'src/components/animate';

// utils
import { logos } from '../../utils/data';

// ----------------------------------------------------------------------

const StyledLogoBox = styled(Box)(({ theme }) => ({
  height: 80,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
  },
}));

// ----------------------------------------------------------------------

export default function HomeLogos() {
  const theme = useTheme();
  return (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 4 },
        bgcolor: 'background.default',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        minHeight: { xs: 'auto', md: '100vh' },
      }}
    >
      <Container maxWidth="xl" component={MotionViewport} sx={{ width: '100%' }}>
        <Stack spacing={3} sx={{ textAlign: 'center', mb: { xs: 6, md: 8 }, width: '100%' }}>
          <m.div variants={varFade().inDown}>
            <Typography variant="overline" sx={{ color: 'text.disabled', textAlign: 'center' }}>
              Trusted Partners
            </Typography>
          </m.div>

          <m.div variants={varFade().inDown}>
            <Typography
              variant="h2"
              sx={{
                my: 3,
                textAlign: 'center',
                fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
                px: { xs: 1, sm: 0 },
              }}
            >
              Join Thousands of
              <br />
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Successful Businesses
              </Box>
            </Typography>
          </m.div>

          <m.div variants={varFade().inDown}>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                textAlign: 'center',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                px: { xs: 2, sm: 0 },
              }}
            >
              From local restaurants to national chains, from small suppliers to large distributors,
              businesses of all sizes trust Cartify to streamline their operations.
            </Typography>
          </m.div>
        </Stack>

        <Grid container spacing={4} justifyContent="center" sx={{ width: '100%' }}>
          {logos.map((logo, index) => (
            <Grid
              key={logo.name}
              item
              xs={6}
              sm={4}
              md={3}
              lg={2}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <m.div variants={varFade().inUp} style={{ width: '100%', maxWidth: '200px' }}>
                <StyledLogoBox
                  sx={{
                    bgcolor: 'background.neutral',
                    border: '1px solid #E0E0E0',
                  }}
                >
                  <Box
                    component="img"
                    src={logo.logo}
                    alt={logo.name}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      filter: 'none',
                    }}
                  />
                </StyledLogoBox>
                <Typography
                  variant="caption"
                  sx={{
                    textAlign: 'center',
                    display: 'block',
                    mt: 1,
                    color: 'text.secondary',
                  }}
                >
                  {logo.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    textAlign: 'center',
                    display: 'block',
                    color: 'primary.main',
                    fontWeight: 'bold',
                  }}
                >
                  {logo.type}
                </Typography>
              </m.div>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 8, textAlign: 'center', width: '100%' }}>
          <m.div variants={varFade().inUp}>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              And many more businesses trust Cartify every day
            </Typography>
          </m.div>
        </Box>
      </Container>
    </Box>
  );
}
